import React, { useState } from 'react';
import { CheckCircle, Star, Zap, Award, TrendingUp, Shield, Brain, Settings, ArrowRight, Info, X, Clock, DollarSign, FileCheck, BookOpen, Rocket, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

const Pricing = () => {
  const navigate = useNavigate();
  const [selectedService, setSelectedService] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedServiceDetail, setSelectedServiceDetail] = useState(null);

  const pricingData = [
    {
      id: 1,
      name: "Gen AI & Agentic AI Strategy",
      icon: <Brain className="text-indigo-400" size={32} />,
      description: "Implement AI governance and management frameworks",
      hourlyRate: "₹2,000 - ₹4,000",
      consultation: {
        oneTime: "₹20,000 - ₹45,000",
        multiple: "₹60,000 - ₹90,000",
        display: "₹20,000 - ₹90,000"
      },
      audit: "₹1,00,000 - ₹2,40,000",
      training: "₹40,000 - ₹1,50,000",
      implementation: "₹1,50,000 - ₹1,80,000",
      implementationOriginal: "₹3,00,000 - ₹6,00,000",
      limitedTimeOffer: true,
      discountRange: "50-70%",
      features: [
        "AI Governance Framework",
        "ISO 42001 Compliance",
        "AI Risk Management",
        "Ethical AI Implementation",
        "AI Performance Monitoring"
      ],
      trending: true,
      popular: true,
      customPricing: true
    },
    {
      id: 2,
      name: "Six Sigma Implementation",
      icon: <Award className="text-yellow-400" size={32} />,
      description: "Data-driven methodology to eliminate defects and improve quality",
      hourlyRate: "₹1,500 - ₹3,500",
      consultation: {
        oneTime: "₹15,000 - ₹27,000",
        multiple: "₹40,000 - ₹60,000",
        display: "₹15,000 - ₹60,000"
      },
      audit: "₹50,000 - ₹1,20,000",
      training: "₹20,000 - ₹80,000",
      implementation: "₹1,00,000 - ₹1,08,000",
      implementationOriginal: "₹2,00,000 - ₹3,60,000",
      limitedTimeOffer: true,
      discountRange: "50-70%",
      features: [
        "DMAIC Methodology Implementation",
        "Statistical Process Control",
        "Quality Management Systems",
        "Defect Reduction Strategies",
        "Process Optimization"
      ],
      customPricing: true
    },
    {
      id: 3,
      name: "Lean Process Excellence",
      icon: <TrendingUp className="text-blue-400" size={32} />,
      description: "Eliminate waste and maximize value in your operations",
      hourlyRate: "₹1,500 - ₹3,500",
      consultation: {
        oneTime: "₹15,000 - ₹27,000",
        multiple: "₹40,000 - ₹60,000",
        display: "₹15,000 - ₹60,000"
      },
      audit: "₹50,000 - ₹1,20,000",
      training: "₹20,000 - ₹80,000",
      implementation: "₹1,00,000 - ₹1,08,000",
      implementationOriginal: "₹2,00,000 - ₹3,60,000",
      limitedTimeOffer: true,
      discountRange: "50-70%",
      features: [
        "Value Stream Mapping",
        "Waste Elimination (8 Wastes)",
        "Continuous Flow Design",
        "Kaizen Implementation",
        "5S Workplace Organization"
      ],
      customPricing: true
    },
    {
      id: 4,
      name: "Operational Excellence Management",
      icon: <Star className="text-purple-400" size={32} />,
      description: "Comprehensive approach to organizational performance",
      hourlyRate: "₹2,000 - ₹3,500",
      consultation: {
        oneTime: "₹20,000 - ₹36,000",
        multiple: "₹50,000 - ₹75,000",
        display: "₹20,000 - ₹75,000"
      },
      audit: "₹75,000 - ₹1,50,000",
      training: "₹30,000 - ₹1,00,000",
      implementation: "₹1,25,000 - ₹1,35,000",
      implementationOriginal: "₹2,50,000 - ₹4,50,000",
      limitedTimeOffer: true,
      discountRange: "50-70%",
      features: [
        "Strategic Performance Management",
        "Operational KPI Development",
        "Process Standardization",
        "Leadership Development",
        "Cultural Transformation"
      ],
      premium: true,
      customPricing: true
    },
    {
      id: 5,
      name: "ISO Certification & Audits",
      icon: <Shield className="text-green-400" size={32} />,
      description: "International standards compliance and certification",
      hourlyRate: "₹1,000 - ₹3,000",
      consultation: {
        oneTime: "₹10,000 - ₹18,000",
        multiple: "₹30,000 - ₹45,000",
        display: "₹10,000 - ₹45,000"
      },
      audit: "₹25,000 - ₹90,000",
      training: "₹15,000 - ₹60,000",
      implementation: "₹50,000 - ₹72,000",
      implementationOriginal: "₹1,00,000 - ₹2,40,000",
      limitedTimeOffer: true,
      discountRange: "50-70%",
      features: [
        "ISO 9001, 14001, 45001",
        "Compliance Assessment",
        "Documentation Support",
        "Internal Audit Training",
        "Certification Readiness"
      ],
      customPricing: true
    },
    {
      id: 6,
      name: "Risk Management & Assessment",
      icon: <Shield className="text-red-400" size={32} />,
      description: "Identify, assess, and mitigate organizational risks",
      hourlyRate: "₹1,800 - ₹4,200",
      consultation: {
        oneTime: "₹17,500 - ₹30,000",
        multiple: "₹45,000 - ₹60,000",
        display: "₹17,500 - ₹60,000"
      },
      audit: "₹75,000 - ₹1,80,000",
      training: "₹25,000 - ₹90,000",
      implementation: "₹1,00,000 - ₹1,08,000",
      implementationOriginal: "₹2,00,000 - ₹3,60,000",
      limitedTimeOffer: true,
      discountRange: "50-70%",
      features: [
        "Risk Assessment Frameworks",
        "Business Continuity Planning",
        "Compliance Risk Management",
        "Crisis Management",
        "Risk Mitigation Strategies"
      ],
      customPricing: true
    },
    {
      id: 7,
      name: "Quality Control & SLA Management",
      icon: <CheckCircle className="text-emerald-400" size={32} />,
      description: "Ensure consistent quality and service level agreements",
      hourlyRate: "₹1,500 - ₹2,500",
      consultation: {
        oneTime: "₹15,000 - ₹27,000",
        multiple: "₹40,000 - ₹60,000",
        display: "₹15,000 - ₹60,000"
      },
      audit: "₹50,000 - ₹1,20,000",
      training: "₹20,000 - ₹80,000",
      implementation: "₹75,000 - ₹90,000",
      implementationOriginal: "₹1,50,000 - ₹3,00,000",
      limitedTimeOffer: true,
      discountRange: "50-70%",
      features: [
        "Quality Control Systems",
        "SLA Framework Development",
        "Performance Monitoring",
        "Customer Satisfaction Metrics",
        "Quality Assurance Processes"
      ],
      customPricing: true
    },
    {
      id: 8,
      name: "RPA & Process Automation",
      icon: <Settings className="text-orange-400" size={32} />,
      description: "Automate repetitive processes for efficiency gains",
      hourlyRate: "₹3,000 - ₹5,000",
      consultation: {
        oneTime: "₹25,000 - ₹60,000",
        multiple: "₹75,000 - ₹1,05,000",
        display: "₹25,000 - ₹1,05,000"
      },
      audit: "₹1,00,000 - ₹2,40,000",
      training: "₹40,000 - ₹1,50,000",
      implementation: "₹2,00,000 - ₹2,70,000",
      implementationOriginal: "₹4,00,000 - ₹9,00,000",
      limitedTimeOffer: true,
      discountRange: "50-70%",
      features: [
        "Process Automation Strategy",
        "RPA Implementation",
        "Bot Development & Deployment",
        "Process Optimization",
        "Automation ROI Analysis"
      ],
      customPricing: true
    }
  ];

  const serviceTypes = [
    {
      type: "consultation",
      title: "Consultation",
      subtitle: "Strategy & Advisory",
      icon: <Info size={20} />,
      description: "Expert strategic guidance and advisory services"
    },
    {
      type: "audit",
      title: "Audit",
      subtitle: "Assessment & Gap Analysis", 
      icon: <CheckCircle size={20} />,
      description: "Comprehensive assessment and gap analysis"
    },
    {
      type: "training",
      title: "Training",
      subtitle: "Per Day for Group",
      icon: <Award size={20} />,
      description: "Professional training and skill development"
    },
    {
      type: "implementation",
      title: "Implementation",
      subtitle: "Hands-on Project",
      icon: <Zap size={20} />,
      description: "Complete project implementation and execution"
    }
  ];

  const handleBookConsultation = (serviceName) => {
    navigate('/book-consultation', { state: { preSelectedService: serviceName } });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      {/* Hero Section */}
      <div className="relative overflow-hidden py-20 px-6">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-turquoise-500/10 to-blue-500/10"></div>
          <div className="absolute top-0 left-1/4 w-72 h-72 bg-turquoise-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-turquoise-500/10 border border-turquoise-500/20 rounded-full px-4 py-2 mb-8">
            <Star className="text-turquoise-400" size={16} />
            <span className="text-sm text-turquoise-400 font-medium">Professional Consulting Services</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            Transform Your
            <span className="text-turquoise-400"> Business</span>
          </h1>
          
          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            Comprehensive operational excellence solutions designed to drive efficiency, 
            reduce costs, and accelerate growth across all business functions.
          </p>

          {/* Service Types Overview */}
          <div className="grid md:grid-cols-4 gap-6 mb-16">
            {serviceTypes.map((serviceType, index) => {
              const colors = [
                { 
                  bg: 'from-blue-500/20 to-blue-600/10',
                  border: 'border-blue-500/30',
                  iconBg: 'bg-blue-500/20',
                  iconColor: 'text-blue-400',
                  glow: 'shadow-blue-500/20'
                },
                { 
                  bg: 'from-emerald-500/20 to-emerald-600/10',
                  border: 'border-emerald-500/30',
                  iconBg: 'bg-emerald-500/20',
                  iconColor: 'text-emerald-400',
                  glow: 'shadow-emerald-500/20'
                },
                { 
                  bg: 'from-amber-500/20 to-amber-600/10',
                  border: 'border-amber-500/30',
                  iconBg: 'bg-amber-500/20',
                  iconColor: 'text-amber-400',
                  glow: 'shadow-amber-500/20'
                },
                { 
                  bg: 'from-turquoise-500/20 to-turquoise-600/10',
                  border: 'border-turquoise-500/30',
                  iconBg: 'bg-turquoise-500/20',
                  iconColor: 'text-turquoise-400',
                  glow: 'shadow-turquoise-500/20'
                }
              ];
              
              const colorScheme = colors[index];
              
              return (
                <div key={index} className={`group relative bg-gradient-to-br ${colorScheme.bg} backdrop-blur-sm border ${colorScheme.border} rounded-2xl p-8 hover:scale-105 hover:shadow-2xl ${colorScheme.glow} transition-all duration-500 cursor-pointer`}>
                  {/* Animated background gradient */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  {/* Floating orb effect */}
                  <div className={`absolute -top-2 -right-2 w-12 h-12 ${colorScheme.iconBg} rounded-full blur-xl opacity-20 group-hover:opacity-40 transition-opacity duration-500`}></div>
                  
                  <div className="relative z-10">
                    {/* Icon container with floating animation */}
                    <div className={`inline-flex items-center justify-center w-16 h-16 ${colorScheme.iconBg} rounded-2xl mb-4 group-hover:rotate-6 group-hover:scale-110 transition-transform duration-500`}>
                      <div className={`${colorScheme.iconColor} group-hover:scale-125 transition-transform duration-300`}>
                        {serviceType.icon}
                      </div>
                    </div>
                    
                    {/* Service type info */}
                    <div className="space-y-2 mb-4">
                      <h3 className="text-xl font-bold text-white group-hover:text-white transition-colors duration-300">
                        {serviceType.title}
                      </h3>
                      <p className={`text-sm font-medium ${colorScheme.iconColor} group-hover:brightness-110 transition-all duration-300`}>
                        {serviceType.subtitle}
                      </p>
                    </div>
                    
                    {/* Description */}
                    <p className="text-sm text-gray-300 group-hover:text-gray-200 transition-colors duration-300 leading-relaxed">
                      {serviceType.description}
                    </p>
                    
                    {/* Decorative line */}
                    <div className={`mt-6 h-1 bg-gradient-to-r ${colorScheme.bg} rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}></div>
                  </div>
                  
                  {/* Corner accent */}
                  <div className={`absolute bottom-0 right-0 w-8 h-8 ${colorScheme.iconColor} opacity-10 transform translate-x-2 translate-y-2`}>
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
                      <path d="M12 2L13.09 8.26L20 7L14.74 12.26L21 13.09L8.26 13.09L7 20L12.26 14.74L13.09 21L13.09 8.26L20 7L12 2Z"/>
                    </svg>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="px-6 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Service Packages & Pricing</h2>
            <p className="text-gray-400 text-lg">Choose the right service package for your business transformation journey</p>
          </div>

          <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {pricingData.map((service) => {
              // Map service categories to background images
              const getBackgroundImage = () => {
                if (service.name.includes('Six Sigma') || service.name.includes('Lean') || service.name.includes('RPA') || service.name.includes('Strategy')) {
                  return 'consultation-pricing.png';
                } else if (service.name.includes('ISO') || service.name.includes('Risk') || service.name.includes('Compliance')) {
                  return 'audit-pricing.png';
                } else if (service.name.includes('Training') || service.name.includes('AI')) {
                  return 'training-pricing.png';
                }
                return 'consultation-pricing.png'; // default
              };
              
              return (
              <div
                key={service.id}
                className={`relative overflow-hidden bg-gray-900/50 border rounded-2xl p-8 hover:border-turquoise-500/50 transition-all duration-300 group ${
                  service.popular ? 'border-turquoise-500/30 bg-gradient-to-b from-turquoise-500/5 to-transparent' :
                  service.premium ? 'border-purple-500/30 bg-gradient-to-b from-purple-500/5 to-transparent' :
                  service.trending ? 'border-indigo-500/30 bg-gradient-to-b from-indigo-500/5 to-transparent' :
                  'border-gray-700/50'
                }`}
              >
                {/* Floating Background Image with 20% opacity */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
                  <img 
                    src={`/${getBackgroundImage()}`}
                    alt=""
                    className="w-full h-full object-cover opacity-20 group-hover:opacity-25 transition-opacity duration-500 group-hover:scale-110 transform duration-700"
                    style={{ filter: 'blur(0px)' }}
                  />
                  {/* Gradient overlay to blend with dark theme */}
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-900/30 to-gray-900/80"></div>
                </div>
                
                {/* Content wrapper with relative positioning */}
                <div className="relative z-10">
                {/* Badge */}
                {service.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-turquoise-500 to-turquoise-600 text-black px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </div>
                  </div>
                )}
                {service.premium && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Premium
                    </div>
                  </div>
                )}
                {service.trending && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Trending
                    </div>
                  </div>
                )}

                {/* Service Header */}
                <div className="text-center mb-6">
                  <div className="mb-4">{service.icon}</div>
                  <h3 className="text-xl font-bold text-white mb-2">{service.name}</h3>
                  <p className="text-gray-400 text-sm">{service.description}</p>
                  {service.limitedTimeOffer && (
                    <div className="mt-3 inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse">
                      <Zap size={12} />
                      Limited Time: Offer {service.discountRange} Off!
                    </div>
                  )}
                </div>

                {/* Prominent Hourly Rate Badge */}
                <div className="mb-6 text-center">
                  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-turquoise-500/20 to-blue-500/20 border border-turquoise-400/30 rounded-full px-4 py-2">
                    <Clock size={16} className="text-turquoise-400" />
                    <span className="text-turquoise-400 font-bold text-sm">Hourly: {service.hourlyRate}/hr</span>
                  </div>
                </div>

                {/* Quick Pricing Overview */}
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between items-center p-3 bg-gray-800/50 rounded-lg border border-gray-700/50 hover:border-turquoise-500/30 transition-all">
                    <div className="text-left">
                      <div className="text-white font-medium flex items-center gap-2">
                        <Info size={14} className="text-turquoise-400" />
                        Consultation
                      </div>
                      <div className="text-xs text-gray-400">One-time & Multiple Sessions</div>
                    </div>
                    <div className="text-turquoise-400 font-bold text-sm">{service.consultation.display}</div>
                  </div>
                  
                  <div className="flex justify-between items-center p-3 bg-gray-800/50 rounded-lg border border-gray-700/50 hover:border-blue-500/30 transition-all">
                    <div className="text-left">
                      <div className="text-white font-medium flex items-center gap-2">
                        <FileCheck size={14} className="text-blue-400" />
                        Audit
                      </div>
                      <div className="text-xs text-gray-400">Assessment & Gap Analysis</div>
                    </div>
                    <div className="text-blue-400 font-bold text-sm">{service.audit}</div>
                  </div>
                  
                  <div className="flex justify-between items-center p-3 bg-gray-800/50 rounded-lg border border-gray-700/50 hover:border-green-500/30 transition-all">
                    <div className="text-left">
                      <div className="text-white font-medium flex items-center gap-2">
                        <BookOpen size={14} className="text-green-400" />
                        Training
                      </div>
                      <div className="text-xs text-gray-400">Per Day for Group</div>
                    </div>
                    <div className="text-green-400 font-bold text-sm">{service.training}</div>
                  </div>
                  
                  <div className="flex justify-between items-center p-3 bg-gradient-to-r from-orange-500/10 to-red-500/10 rounded-lg border border-orange-500/30 hover:border-orange-500/50 transition-all">
                    <div className="text-left">
                      <div className="text-white font-medium flex items-center gap-2">
                        <Rocket size={14} className="text-orange-400" />
                        Implementation
                      </div>
                      <div className="text-xs text-gray-400">End-to-End Project</div>
                    </div>
                    <div className="text-right">
                      <div className="text-orange-400 font-bold text-sm">{service.implementation}</div>
                    </div>
                  </div>
                </div>

                {/* View Detailed Pricing Button */}
                <button
                  onClick={() => {
                    setSelectedServiceDetail(service);
                    setShowDetailModal(true);
                  }}
                  className="w-full mb-4 py-2 px-4 bg-gray-800/70 hover:bg-gray-700 border border-gray-600 rounded-lg text-turquoise-400 text-sm font-medium transition-all flex items-center justify-center gap-2"
                >
                  <Zap size={14} />
                  View Detailed Pricing Breakdown
                </button>

                {/* Features */}
                <div className="mb-6">
                  <h4 className="text-white font-semibold mb-3">Key Features:</h4>
                  <ul className="space-y-2">
                    {service.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2 text-gray-300 text-sm">
                        <CheckCircle size={16} className="text-turquoise-400 mt-0.5 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA Button */}
                <Button
                  className={`w-full ${
                    service.popular ? 'btn-primary' :
                    service.premium ? 'bg-purple-600 hover:bg-purple-700' :
                    service.trending ? 'bg-indigo-600 hover:bg-indigo-700' :
                    'btn-secondary'
                  } group`}
                  onClick={() => handleBookConsultation(service.name)}
                >
                  Get Started with {service.name.split(' ')[0]}
                  <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                </div>
              </div>
            );
            })}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="px-6 pb-20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-gray-900/80 to-gray-800/80 border border-turquoise-500/20 rounded-3xl p-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Transform Your Operations?
            </h2>
            <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
              Get a personalized consultation to discuss your specific needs and receive a customized proposal 
              tailored to your business objectives and budget.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                className="btn-primary text-lg px-8 py-4"
                onClick={() => navigate('/book-consultation')}
              >
                Book Free Strategy Call
              </Button>
              <Button 
                variant="outline" 
                className="border-turquoise-500 text-turquoise-400 hover:bg-turquoise-500/10 text-lg px-8 py-4"
                onClick={() => navigate('/contact')}
              >
                Contact Us
              </Button>
            </div>
            <div className="mt-8 text-sm text-gray-400">
              ✓ No commitment required • ✓ Free initial consultation • ✓ Customized solutions
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Pricing Modal */}
      {showDetailModal && selectedServiceDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setShowDetailModal(false)}>
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-turquoise-500/30 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 border-b border-gray-700 p-6 z-10">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-turquoise-500/20 to-blue-500/20 rounded-xl">
                    {selectedServiceDetail.icon}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">{selectedServiceDetail.name}</h3>
                    <p className="text-gray-400 text-sm mt-1">{selectedServiceDetail.description}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X size={24} className="text-gray-400" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Hourly Rate - Prominent Display */}
              <div className="bg-gradient-to-r from-turquoise-500/10 to-blue-500/10 border border-turquoise-500/30 rounded-xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Clock size={20} className="text-turquoise-400" />
                      <h4 className="text-lg font-bold text-white">Hourly Rate</h4>
                    </div>
                    <p className="text-gray-400 text-sm">Perfect for short-term advisory and quick consultations</p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-turquoise-400">{selectedServiceDetail.hourlyRate}</div>
                    <div className="text-xs text-gray-500 mt-1">per hour</div>
                  </div>
                </div>
              </div>

              {/* Consultation Pricing Breakdown */}
              <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Info size={20} className="text-turquoise-400" />
                  <h4 className="text-lg font-bold text-white">Consultation Services</h4>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-gray-900/50 border border-gray-600 rounded-lg p-4">
                    <div className="text-sm text-gray-400 mb-2">One-Time Consultation</div>
                    <div className="text-2xl font-bold text-turquoise-400">{selectedServiceDetail.consultation.oneTime}</div>
                    <div className="text-xs text-gray-500 mt-1">Single session strategy & advisory</div>
                  </div>
                  <div className="bg-gray-900/50 border border-gray-600 rounded-lg p-4">
                    <div className="text-sm text-gray-400 mb-2">Multiple Sessions</div>
                    <div className="text-2xl font-bold text-turquoise-400">{selectedServiceDetail.consultation.multiple}</div>
                    <div className="text-xs text-gray-500 mt-1">Ongoing engagement package</div>
                  </div>
                </div>
              </div>

              {/* Other Services Grid */}
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <FileCheck size={18} className="text-blue-400" />
                    <h5 className="font-semibold text-white">Audit Services</h5>
                  </div>
                  <div className="text-xl font-bold text-blue-400 mb-1">{selectedServiceDetail.audit}</div>
                  <p className="text-xs text-gray-500">Comprehensive assessment & gap analysis</p>
                </div>

                <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <BookOpen size={18} className="text-green-400" />
                    <h5 className="font-semibold text-white">Training</h5>
                  </div>
                  <div className="text-xl font-bold text-green-400 mb-1">{selectedServiceDetail.training}</div>
                  <p className="text-xs text-gray-500">Per day group training programs</p>
                </div>

                <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Rocket size={18} className="text-orange-400" />
                    <h5 className="font-semibold text-white">Implementation</h5>
                  </div>
                  <div className="text-xl font-bold text-orange-400 mb-1">{selectedServiceDetail.implementation}</div>
                  <p className="text-xs text-gray-500">End-to-end project execution</p>
                </div>
              </div>

              {/* Custom Pricing Note */}
              {selectedServiceDetail.customPricing && (
                <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-5">
                  <div className="flex items-start gap-3">
                    <Mail size={20} className="text-purple-400 mt-1" />
                    <div>
                      <h5 className="font-bold text-white mb-2">Need Custom Pricing?</h5>
                      <p className="text-gray-300 text-sm mb-3">
                        For large-scale enterprise projects, multi-year engagements, or unique requirements, 
                        we offer tailored pricing packages designed specifically for your needs.
                      </p>
                      <Button
                        className="btn-primary text-sm"
                        onClick={() => {
                          setShowDetailModal(false);
                          navigate('/contact');
                        }}
                      >
                        Contact for Custom Pricing
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Key Features */}
              <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
                <h5 className="font-bold text-white mb-4">What's Included:</h5>
                <ul className="grid md:grid-cols-2 gap-3">
                  {selectedServiceDetail.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-gray-300 text-sm">
                      <CheckCircle size={16} className="text-turquoise-400 mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA Buttons */}
              <div className="flex gap-4 pt-4">
                <Button
                  className="flex-1 btn-primary"
                  onClick={() => {
                    setShowDetailModal(false);
                    handleBookConsultation(selectedServiceDetail.name);
                  }}
                >
                  Book Consultation
                  <ArrowRight size={16} className="ml-2" />
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700"
                  onClick={() => setShowDetailModal(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pricing;